import React, { useMemo } from 'react';
import { CTA, styled, TextTag } from '@volkswagen-onehub/components-core';
import { useStore } from '../../../../store/store';
import { setQsgSearchPage } from '../../../../store/slices/qsg/actions';
import { Icon } from '../../../Icon';
import { TextBox } from '../../../Box/components/TextBox';
import { TextAppearance } from '@vw-marketing/us-components';
import { onVWBasicButtonClickEvent_PaginationButton } from '../../../../tagging/tagging-helpers';
import { useTrackingManager } from '../../../../hooks/use-tracking-manager';
import { getPageNumbers } from './helpers';

const PaginationContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
  min-height: 36px;
  width: 100%;
  margin: 0 auto;
`;

const PageNumbersContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const PageButton = styled(CTA)<{ $isActive?: boolean }>`
  min-height: 36px;
  min-width: 36px;
  padding: 0;

  border: none;
  background: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.background.secondary : 'transparent'};
  color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.content.active : theme.colors.content.tertiary};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  > span {
    font-weight: ${({ $isActive }) => ($isActive ? 'bold' : 'normal')};
  }

  transition: all 0.2s ease;

  &&:hover,
  &&:active,
  &&:focus:not(:focus-visible) {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.content.active};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Ellipsis = styled.span`
  display: block;
  height: 36px;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.content.tertiary};
  padding: 0 4px;
`;

const NavButton = styled(CTA)`
  min-height: 36px;
  min-width: 36px;
  padding: 0;

  border: none;
  background: ${({ theme }) => theme.colors.content.active};
  color: ${({ theme }) => theme.colors.background.primary};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.button.primary.hover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PreviousButton = styled(NavButton)`
  margin-right: 4px;
`;

const NextButton = styled(NavButton)`
  margin-left: 4px;
`;

interface SearchPaginationProps {
  maxVisibleItems?: number;
  showEllipsis?: boolean;
}

export const SearchPagination = ({
  maxVisibleItems = 3,
  showEllipsis = true,
}: SearchPaginationProps = {}) => {
  const [state, dispatch] = useStore();
  const trackingManager = useTrackingManager();
  const { qsgSearchTerm, qsgSearchPage, qsgSearchResultsMap, qsgNavigation } =
    state.qsg;

  const model = qsgNavigation
    ? {
        modelId: qsgNavigation.modelId,
        modelName: qsgNavigation.modelName,
        modelYear: qsgNavigation.modelYear,
      }
    : undefined;

  // Find the first loaded result for this search term (any page)
  // This ensures pagination doesn't disappear when changing pages
  const firstLoadedResult = qsgSearchTerm
    ? Object.entries(qsgSearchResultsMap).find(([key]) =>
        key.startsWith(`${qsgSearchTerm}:`)
      )?.[1]
    : null;

  const totalCount = firstLoadedResult?.totalCount ?? 0;
  const limit = firstLoadedResult?.limit ?? 10;
  const totalPages = Math.ceil(totalCount / limit);
  const currentPage = qsgSearchPage;

  const pageNumbers = useMemo(
    () =>
      getPageNumbers(currentPage, totalPages, {
        maxVisibleItems,
        showEllipsis,
      }),
    [currentPage, totalPages, maxVisibleItems, showEllipsis]
  );

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onVWBasicButtonClickEvent_PaginationButton(
        trackingManager,
        qsgSearchTerm,
        page,
        model
      );
      dispatch(setQsgSearchPage(page));
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onVWBasicButtonClickEvent_PaginationButton(
        trackingManager,
        qsgSearchTerm,
        'Previous',
        model
      );
      dispatch(setQsgSearchPage(currentPage - 1));
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onVWBasicButtonClickEvent_PaginationButton(
        trackingManager,
        qsgSearchTerm,
        'Next',
        model
      );
      dispatch(setQsgSearchPage(currentPage + 1));
    }
  };

  if (totalPages <= 0) {
    return null; // Don't show pagination if there are no results
  }

  return (
    <PaginationContainer>
      {currentPage > 1 && (
        <PreviousButton
          onClick={handlePrevious}
          aria-label='Previous page'
          disabled={currentPage === 1}
        >
          <Icon
            name='ArrowLeft'
            ignoreDefaultColor={true}
            style={{ width: '16px', height: '16px' }}
          />
        </PreviousButton>
      )}
      <PageNumbersContainer>
        {pageNumbers.map((page, index) => {
          if (page === 'ellipsis') {
            return <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>;
          }

          return (
            <PageButton
              key={page}
              $isActive={page === currentPage}
              onClick={() => handlePageChange(page)}
              aria-label={`Go to page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              <TextBox appearance={TextAppearance.copy200} tag={TextTag.span}>
                {page}
              </TextBox>
            </PageButton>
          );
        })}
      </PageNumbersContainer>
      {currentPage < totalPages && (
        <NextButton
          onClick={handleNext}
          aria-label='Next page'
          disabled={currentPage === totalPages}
        >
          <Icon
            name='ArrowRight'
            ignoreDefaultColor={true}
            style={{ width: '16px', height: '16px' }}
          />
        </NextButton>
      )}
    </PaginationContainer>
  );
};
